import React, { useEffect, useState } from 'react';
import { BOAPI } from '../service/UsersService';

// הגדרות ידועות עם תווית/הסבר ידידותיים - כל הגדרה עתידית שתתווסף
// ל-AppSettings בלי להופיע כאן, עדיין תוצג אוטומטית בקטע "הגדרות נוספות"
// למטה (עריכת key/value גולמית) - כך שאין צורך לגעת ב-UI בכל הגדרה חדשה,
// רק להוסיף אותה כאן אם רוצים תצוגה נעימה יותר
const KNOWN_SETTINGS = [
  {
    key: 'dailyCouponLimit',
    label: 'מגבלת קופונים יומית למשתמש',
    description: 'כמה קודי קופון לכל היותר יכול משתמש אחד לבקש ביום אחד, בסך הכל בכל בתי העסק יחד. השאירו ריק כדי לבטל את ההגבלה.',
    type: 'number',
  },
  {
    key: 'geofenceRadiusMeters',
    label: 'רדיוס התראת קרבה לעסק (מטרים)',
    description: 'כשמשתמש עם התראות קרבה פעילות מתקרב לנ"צ של בית עסק לטווח הזה, הוא מקבל התראה על ההטבה. שימו לב: GPS ומערכות ההפעלה אינם מדויקים מתחת ל-50-100 מטר בערך, לכן רדיוס קטן מאוד (כמו 15) עלול לפספס משתמשים. ההגדרה נטענת באפליקציה בפתיחתה.',
    type: 'number',
  },
  {
    key: 'reviewWindowMinutes',
    label: 'חלון דירוג אחרי מימוש (דקות)',
    description: 'כמה דקות מרגע מימוש הקוד בבית העסק הלקוח יכול לדרג את העסק ולהגיב עליו. ברירת מחדל: 30.',
    type: 'number',
  },
  {
    key: 'businessOwnerTerms',
    label: 'תנאי הצטרפות לבעלי עסקים',
    description: 'הטקסט שמוצג בדף התנאים (מקושר מדף אישור בית עסק חדש שנשלח לבעל העסק - ראו כפתור "העתקת קישור" בטאב בתי עסק).',
    type: 'textarea',
  },
];

// טאב "קונפיגורציה" - הגדרות מערכת גלובליות (AppSettings, key-value) -
// כרגע רק מגבלת הקופונים היומית (נאכפת ב-spTransactionRequestCode), אבל
// בנוי כללי כדי שאפשר יהיה להוסיף הגדרות נוספות בעתיד בלי שינוי סכימה
const ConfigurationTab = () => {
  const [settings, setSettings] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await BOAPI.fetchAppSettings();
      if (resp?.[0]?.[0]?.code === 0) {
        const rows = resp[1] || [];
        setSettings(rows);
        const map = {};
        rows.forEach((r) => { map[r.key] = r.value; });
        setValues(map);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'שגיאה בטעינת ההגדרות');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const saveKey = async (key, label) => {
    const value = values[key] ?? '';
    setSavingKey(key);
    setError('');
    try {
      const resp = await BOAPI.updateAppSetting(key, String(value));
      if (resp?.[0]?.[0]?.code === 0) {
        showSuccess(`"${label || key}" נשמר בהצלחה`);
      } else {
        setError(resp?.[0]?.[0]?.msg || 'השמירה נכשלה');
      }
    } catch {
      setError('שגיאה בתקשורת עם השרת');
    } finally {
      setSavingKey(null);
    }
  };

  const knownKeys = new Set(KNOWN_SETTINGS.map((s) => s.key));
  const extraSettings = settings.filter((s) => !knownKeys.has(s.key));

  return (
    <div style={s.container}>
      <div style={s.headerRow}>
        <h2 style={s.title}>קונפיגורציה</h2>
      </div>
      <p style={s.hint}>הגדרות מערכת גלובליות, נכנסות לתוקף מיידית לאחר השמירה.</p>

      {error && (
        <div style={s.errorBanner}>❌ {error}
          <button style={s.closeErr} onClick={() => setError('')}>✕</button>
        </div>
      )}
      {successMsg && <div style={s.successBanner}>✅ {successMsg}</div>}

      {loading ? (
        <div style={s.center}>טוען...</div>
      ) : (
        <div style={s.list}>
          {KNOWN_SETTINGS.map((setting) => (
            <div key={setting.key} style={s.card}>
              <label style={s.fieldLabel}>{setting.label}</label>
              <p style={s.fieldDesc}>{setting.description}</p>
              <div style={setting.type === 'textarea' ? s.columnRow : s.row}>
                {setting.type === 'textarea' ? (
                  <textarea
                    value={values[setting.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                    style={s.textarea}
                    rows={4}
                  />
                ) : (
                  <input
                    type={setting.type}
                    value={values[setting.key] ?? ''}
                    onChange={(e) => setValues((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                    style={s.input}
                  />
                )}
                <button
                  type="button"
                  style={setting.type === 'textarea' ? { ...s.saveBtn, alignSelf: 'flex-start' } : s.saveBtn}
                  onClick={() => saveKey(setting.key, setting.label)}
                  disabled={savingKey === setting.key}
                >
                  {savingKey === setting.key ? 'שומר...' : 'שמירה'}
                </button>
              </div>
            </div>
          ))}

          {extraSettings.length > 0 && (
            <>
              <h3 style={s.subTitle}>הגדרות נוספות</h3>
              {extraSettings.map((setting) => (
                <div key={setting.key} style={s.card}>
                  <label style={s.fieldLabel}>{setting.key}</label>
                  <div style={s.row}>
                    <input
                      type="text"
                      value={values[setting.key] ?? ''}
                      onChange={(e) => setValues((prev) => ({ ...prev, [setting.key]: e.target.value }))}
                      style={s.input}
                    />
                    <button
                      type="button"
                      style={s.saveBtn}
                      onClick={() => saveKey(setting.key)}
                      disabled={savingKey === setting.key}
                    >
                      {savingKey === setting.key ? 'שומר...' : 'שמירה'}
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const s = {
  container:    { padding: 20, direction: 'rtl', fontFamily: 'Arial, sans-serif' },
  headerRow:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 12 },
  title:        { margin: 0, color: '#1a1f36', fontSize: 24, fontWeight: 'bold' },
  subTitle:     { margin: '10px 0 0', color: '#1a1f36', fontSize: 16, fontWeight: 700 },
  hint:         { color: '#8792a2', fontSize: 12.5, marginBottom: 18, lineHeight: 1.6 },
  center:       { padding: 40, textAlign: 'center', color: '#999' },
  errorBanner:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#ffebee', color: '#c62828', padding: 14, borderRadius: 6, marginBottom: 16 },
  closeErr:     { background: 'transparent', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18 },
  successBanner:{ background: '#e8f5e9', color: '#2e7d32', padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 13 },

  list:         { display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 520 },
  card:         { border: '1px solid #e3e8ee', borderRadius: 12, padding: 16, background: '#fff' },
  fieldLabel:   { display: 'block', fontSize: 14, fontWeight: 700, color: '#1a1f36', marginBottom: 4 },
  fieldDesc:    { color: '#8792a2', fontSize: 12.5, lineHeight: 1.5, margin: '0 0 12px 0' },
  row:          { display: 'flex', gap: 10 },
  columnRow:    { display: 'flex', flexDirection: 'column', gap: 10 },
  input:        { flex: 1, padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', direction: 'ltr', textAlign: 'right' },
  textarea:     { width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e3e8ee', fontSize: 14, boxSizing: 'border-box', direction: 'rtl', textAlign: 'right', fontFamily: 'inherit', resize: 'vertical' },
  saveBtn:      { padding: '9px 18px', background: '#1f6feb', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700 },
};

export default ConfigurationTab;
